import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import saveUpdatedUser from '../api/saveUpdatedUser';
import { format, parseISO } from 'date-fns';

const userStatusLabels = {
  active: 'Activo',
  blocked: 'Bloqueado',
  pending: 'Pendiente de validación de correo'
};

export default function useUserDetails(
  selectedUser,
  onSuccessfulSave,
  selectionMode,
  userStatusCatalog,
  documentTypes
) {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorBody, setErrorBody] = useState('');
  const [successBody, setSuccessBody] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    control,
    reset,
    setValue,
    getValues
  } = useForm();

  useEffect(() => {
    if (!selectedUser) return;
    
    // Build fullPhone from countryCode and phone if not present
    let fullPhone = selectedUser.fullPhone;
    if (!fullPhone && selectedUser.countryCode && selectedUser.phone) {
      const cleanCode = (selectedUser.countryCode || '').replace(/^\+/, '');
      fullPhone = `${cleanCode}${selectedUser.phone || ''}`;
    }
    
    reset({ 
      ...selectedUser, 
      creation: format(parseISO(selectedUser.creation), 'yyyy-MM-dd H:mm:ss'),
      phone: selectedUser.phone || '',
      countryCode: selectedUser.countryCode || '',
      fullPhone: fullPhone || ''
    });
  }, [selectedUser, reset]);

  const _createErrorMessage = (errorMessage) => {
    setShowErrorModal(true);
    setErrorBody(errorMessage);
  };

  const closeErrorMessage = () => {
    setShowErrorModal(false);
    setErrorBody('');
  };

  const _createSuccessMessage = (successMessage) => {
    setShowSuccessModal(true);
    setSuccessBody(successMessage);
  };

  const onCloseSuccessMessage = () => {
    setShowSuccessModal(false);
    setSuccessBody('');
    onSuccessfulSave();
  };

  const onSaveComplete = async (data) => {
    // Ensure phone fields are always included from form state or selectedUser
    let phone = data.phone || getValues('phone') || selectedUser?.phone || '';
    let countryCode = data.countryCode || getValues('countryCode') || selectedUser?.countryCode || '';
    let fullPhone = data.fullPhone || getValues('fullPhone') || '';
    
    // If fullPhone is missing, build it from countryCode and phone
    if (!fullPhone && countryCode && phone) {
      const cleanCode = countryCode.replace(/^\+/, '');
      fullPhone = `${cleanCode}${phone}`;
    }
    
    // If still missing, try to get from selectedUser
    if (!fullPhone && selectedUser) {
      if (selectedUser.fullPhone) {
        fullPhone = selectedUser.fullPhone;
      } else if (selectedUser.countryCode && selectedUser.phone) {
        const cleanCode = (selectedUser.countryCode || '').replace(/^\+/, '');
        fullPhone = `${cleanCode}${selectedUser.phone || ''}`;
        phone = phone || selectedUser.phone || '';
        countryCode = countryCode || selectedUser.countryCode || '';
      }
    }
    
    const executeSaveComplete = await saveUpdatedUser(
      data.id,
      {
        ...data,
        phone: phone,
        countryCode: countryCode,
        fullPhone: fullPhone,
        idUserStatus: parseInt(data.idUserStatus),
        idDocumentTypes: parseInt(data.idDocumentTypes)
      },
      _createErrorMessage
    );

    if (executeSaveComplete) {
      _createSuccessMessage('¡Se ha actualizado el registro con éxito!');
    }
  };

  const onSaveSubmit = (data) => {
    onSaveComplete(data);
    return;
  };

  const documentTypesOptions = useMemo(() => {
    return (
      documentTypes &&
      documentTypes.map((item) => ({

        id: item.id,
        label: item.documentName
      }))
    );
  }, [documentTypes]);


  const userStatusOptions = useMemo(() => {
    return (
      userStatusCatalog &&
      userStatusCatalog.map((item) => ({
        id: item.id,
        label: userStatusLabels[item.statusName]
      }))
    );
  }, [userStatusCatalog]);

  return {
    register,
    handleSubmit,
    errors,
    watch,
    reset,
    setValue,
    isDirty,
    closeErrorMessage,
    onCloseSuccessMessage,
    showSuccessModal,
    showErrorModal,
    errorBody,
    successBody,
    onSaveComplete,
    onSaveSubmit,
    documentTypesOptions,
    userStatusOptions,
    control,
    getValues
  };
}
